import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { DialogActions } from "@mui/material";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import { makeStyles } from "@mui/styles";
import { Form, Formik } from "formik";
import * as yup from 'yup';
import AddressAutocomplete from "components/Autocomplete/AddressAutocomplete";
import Button from "components/CustomButtons/Button";
import Dialog from "components/Dialog/Dialog";
import { selectors as ProfileSelectors } from "store/modules/profile";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles(() => ({
  avatarImg: {
    margin: '10px auto',
    width: '100px',
    position: 'relative',
    '& img': {
      borderRadius: '100px',
    },
    '& button': {
      position: 'absolute',
      right: '6px',
      background: '#ffffff',
      bottom: '0',
      padding: '2px'
    }
  }
}));
function EditBillingInfoDialog(props) {
  const { open, onClose, onCancel, onConfirm, profileData } = props;
  const { t } = useTranslation();
  const { billingFirstName, billingLastName, billingJobRole, billingEmail } = profileData;
  const classes = useStyles();
  const [selectedLocation, setSelectedLocation] = useState('');
  const [location, setLocation] = useState({});
  const [address, setAddress] = useState("");
  const isLoading = useSelector(ProfileSelectors.getIsUpdatingProfileLoading)
  const onAddressChange = (selectedLocation, location) => {
    // setFieldValue()
    setSelectedLocation(selectedLocation)
    setLocation(location);
    // dispatch(EmployerActions.setLocation(selectedLocation, location));
  }
  useEffect(() => {
    const address = profileData.street + ' ' + profileData.city + ' ' + profileData.region + ' ' + profileData.country
    setSelectedLocation({ value: undefined, label: address })
    setAddress(address)
  }, [profileData])


  return (
    <Dialog
      open={open}
      title={t("page_profile_dialog_billing_title")}
      onClose={onClose}
      onCancel={onCancel}
      alertType={'form'}
      hideActions
      size="sm"
      modalType="scrollable"
      disableBackdropClick={false}
    >
      <Grid item xs={12} style={{ width: "100%", paddingLeft: 0, paddingRight: 0 }}>
        <Formik
          initialValues={{
            billingFirstName: profileData.billingFirstName,
            billingLastName: profileData.billingLastName,
            billingJobRole: profileData.billingJobRole,
            billingEmail: profileData.billingEmail,
          }}
          onSubmit={(values, actions) => {
            const request = {
              ...values,
              street: location.street,
              city: location.city,
              country: location.country,
              region: location.region,
              latitude: location.lat,
              longitude: location.lng,
              postalCode: location.postalCode,
            }
            onConfirm(request);
            actions.setSubmitting(false);
          }}
          validationSchema={yup.object().shape({
            billingFirstName: yup.string().required(t("common_errors_required_firstName")).max(55, t("common_errors_required_maxFirstNameLength", { length: 55 })).nullable(),
            billingLastName: yup.string().required(t("common_errors_required_lastName")).max(55, t("common_errors_required_lastName", { length: 55 })).nullable(),
            billingJobRole: yup.string().required(t("common_errors_required_jobRole")).max(55, t("common_errors_required_maxJobRoleLength", { length: 55 })).nullable(),
            billingEmail: yup.string().email(t("common_errors_enter_valid_email")).max(100, t("common_errors_required_maxEmailLength", { length: 100 })).required(t("common_errors_required_email")).nullable(),
          })}>
          {props => {
            const {
              values,
              touched,
              errors,
              handleChange,
              handleBlur,
            } = props;

            return (
              <Form>
                <Grid
                  item
                  container
                  direction="row"
                  xs={12}
                >
                  <Grid container item xs={12} md={12} spacing={1}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        id="billingFirstName"
                        name="billingFirstName"
                        label={`${t("common_labels_first_name")} *`}
                        className={classes.textField}
                        value={values.billingFirstName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={billingFirstName}
                        fullWidth
                        error={errors.billingFirstName && touched.billingFirstName}
                        helperText={errors.billingFirstName && touched.billingFirstName && errors.billingFirstName}
                        margin="normal"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        id="billingLastName"
                        name="billingLastName"
                        label={`${t("common_labels_last_name")} *`}
                        className={classes.textField}
                        value={values.billingLastName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={billingLastName}
                        fullWidth
                        error={errors.billingLastName && touched.billingLastName}
                        helperText={errors.billingLastName && touched.billingLastName && errors.billingLastName}
                        margin="normal"
                      />
                    </Grid>
                  </Grid>
                  <Grid item xs={12} md={12} >
                    <TextField
                      id="billingJobRole"
                      name="billingJobRole"
                      label={`${t("common_labels_job_role")} *`}
                      className={classes.textField}
                      value={values.billingJobRole}
                      disabled={billingJobRole}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      fullWidth
                      error={errors.billingJobRole && touched.billingJobRole}
                      helperText={errors.billingJobRole && touched.billingJobRole && errors.billingJobRole}
                      margin="normal"
                    />
                  </Grid>
                  <Grid container item xs={12} md={12}>
                    <TextField
                      id="billingEmail"
                      name="billingEmail"
                      label={`${t("common_labels_billing_email")} *`}
                      className={classes.textField}
                      value={values.billingEmail}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={billingEmail}
                      fullWidth
                      error={errors.billingEmail && touched.billingEmail}
                      helperText={errors.billingEmail && touched.billingEmail && errors.billingEmail}
                      margin="normal"
                    />
                  </Grid>
                  {address && (
                    <Grid container item xs={12} md={12}>
                      <TextField
                        id="address"
                        name="address"
                        label={`${t("common_labels_address")} *`}
                        className={classes.textField}
                        value={address}
                        disabled={address}
                        fullWidth
                        margin="normal"
                      />
                    </Grid>
                  )}
                  {!address && (
                    <Grid container item xs={12} md={12}>
                      <AddressAutocomplete
                        id="address"
                        name="address"
                        label={`${t("common_labels_address")}`}
                        value={selectedLocation}
                        error={errors.address && touched.address}
                        helperText={errors.address && touched.address && errors.address}
                        margin="normal"
                        onChange={onAddressChange}
                        required
                      />
                    </Grid>
                  )}
                </Grid>
                <DialogActions className={classes.footer}>
                  <Button
                    type="button"
                    className="outline"
                    onClick={onCancel}
                    color="transparent"
                    round
                  >
                    {t("common_labels_close")}
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    round
                    disabled={!_.isEmpty(errors) || isLoading || !selectedLocation}
                  >
                    {t("common_labels_save")}
                  </Button>
                </DialogActions>
              </Form>
            );
          }}
        </Formik>
      </Grid>
    </Dialog>
  );
}

export default EditBillingInfoDialog;
